import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import { DIALOG_BUTTON_BORDER_RADIUS, DIALOG_BUTTON_MIN_HEIGHT } from "./constants";
import MiniTableProCore from "./MiniTableProCore";
import { UpdateMiniTableDetails } from "services/miniTablePro";

const useStyles = makeStyles({
  dialogPaper: {
    maxWidth: "1180px",
    width: "99vw",
    "& .MuiDialogContent-root": { padding: "0 !important" },
  },
  dialogHeader: {
    backgroundColor: "#4A7396",
    color: "white",
    padding: "8px 24px",
    textAlign: "center",
    width: "100%",
  },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
  headerSubtitle: {
    fontSize: "16px",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
    opacity: 0.95,
  },
  dialogBody: {
    padding: "24px 120px 24px 24px",
    maxHeight: "70vh",
    overflowY: "auto",
    overflowX: "hidden",
  },
  tableWrap: {
    maxWidth: "1080px",
    width: "100%",
    margin: "0 auto",
  },
  dialogFooter: {
    padding: "16px 24px",
    background: "#f9fafb",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    alignItems: "stretch",
  },
  // 1fr | auto | 1fr so Update stays truly centered while Cancel stays left
  dialogFooterActions: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    alignItems: "center",
    gap: "12px",
    width: "100%",
  },
  dialogFooterActionsLeft: {
    justifySelf: "start",
  },
  dialogFooterActionsCenter: {
    justifySelf: "center",
  },
  dialogFooterActionsRight: {
    minWidth: 0,
  },
  reorderSortToolbar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "14px",
    width: "100%",
  },
  reorderSortToolbarLeft: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px",
    flex: "1 1 auto",
    minWidth: 0,
  },
  reorderSortToolbarRight: {
    flexShrink: 0,
    marginLeft: "auto",
  },
  reorderSortHint: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#6b7280",
    fontFamily: "objektiv-mk2, sans-serif",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginRight: "4px",
  },
  howToReorderLink: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#4A7396",
    fontFamily: "objektiv-mk2, sans-serif",
    textDecoration: "underline",
    cursor: "pointer",
    background: "none",
    border: "none",
    padding: "4px 2px",
    whiteSpace: "nowrap",
    textAlign: "right",
    "&:hover": { color: "#3a5a7a" },
  },
});

/**
 * Dialog for reordering properties. Uses MiniTableProCore with reorderable=true.
 * Save logic runs inside the dialog on Update click; Update button is centered.
 */
export default function ReorderPropertyDialog({
  open,
  onClose,
  areaName = "",
  properties = [],
  onReorder,
  onDragStart,
  onOrderChange,
  listgenUserID,
  listgenUserAreaID,
  miniTableMeta = {},
  maxRows,
  currentArea,
  quarterlyAreaTable,
  agentData,
  /** Opens the "How to Reorder Properties" overlay (ReorderTutorial) */
  onOpenHowToReorder,
  ...rest
}) {
  const classes = useStyles();
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  /** Toggle ascending / descending per button (labels stay "Sort by …" only) */
  const [propertyTypeDir, setPropertyTypeDir] = useState(null);
  const [statusDir, setStatusDir] = useState(null);

  useEffect(() => {
    if (open) {
      setPropertyTypeDir(null);
      setStatusDir(null);
    }
  }, [open]);

  const sortListByField = useCallback(
    (getValue, ascending) => {
      const copy = [...(properties || [])];
      copy.sort((a, b) => {
        const va = String(getValue(a) ?? "").trim().toLowerCase();
        const vb = String(getValue(b) ?? "").trim().toLowerCase();
        let cmp = va.localeCompare(vb, undefined, { sensitivity: "base" });
        if (!ascending) cmp = -cmp;
        if (cmp !== 0) return cmp;
        return (Number(a._sortingOrder) ?? 0) - (Number(b._sortingOrder) ?? 0);
      });
      onReorder?.(copy.map((p, i) => ({ ...p, _sortingOrder: i })));
    },
    [properties, onReorder]
  );

  const handleSortByPropertyType = useCallback(() => {
    const next = propertyTypeDir === "asc" ? "desc" : "asc";
    setPropertyTypeDir(next);
    sortListByField((p) => p.propertyType, next === "asc");
  }, [propertyTypeDir, sortListByField]);

  const handleSortByStatus = useCallback(() => {
    const next = statusDir === "asc" ? "desc" : "asc";
    setStatusDir(next);
    sortListByField((p) => p.status, next === "asc");
  }, [statusDir, sortListByField]);

  const handleUpdate = useCallback(() => {
    if (isSavingOrder) return;
    setIsSavingOrder(true);
    UpdateMiniTableDetails({
      ...miniTableMeta,
      miniTable: properties,
    })
      .then(() => {
        onOrderChange?.(properties);
        onClose?.();
      })
      .catch(() => {})
      .finally(() => setIsSavingOrder(false));
  }, [isSavingOrder, miniTableMeta, properties, onOrderChange, onClose]);

  const handleCancel = () => {
    if (!isSavingOrder) onClose?.();
  };

  return (
    <MailProDialog
      open={open}
      onClose={() => { if (!isSavingOrder) onClose?.(); }}
      disableEscapeKeyDown={isSavingOrder}
      classes={{ paper: classes.dialogPaper }}
    >
      <MailProDialogHeader>
        <div className={classes.dialogHeader}>
          <div className={classes.headerTitle}>{areaName}</div>
          <div className={classes.headerSubtitle}>
            Drag rows or use sort buttons. Details in How to Reorder Properties.
          </div>
        </div>
      </MailProDialogHeader>
      <MailProDialogBody>
        <div className={`${classes.dialogBody} mini-table-dialog-body`}>
          <div className={classes.tableWrap}>
            <div className={classes.reorderSortToolbar}>
              <div className={classes.reorderSortToolbarLeft}>
                <span className={classes.reorderSortHint}>Sort</span>
                <MailProButton
                  variant="outlined"
                  label="Sort by STATUS"
                  onClick={handleSortByStatus}
                  disabled={isSavingOrder || !properties?.length}
                  customStyle={{
                    fontSize: "11px",
                    padding: "5px 12px",
                    textTransform: "none",
                    fontWeight: "600",
                    minWidth: "auto",
                    borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
                    minHeight: DIALOG_BUTTON_MIN_HEIGHT,
                    height: DIALOG_BUTTON_MIN_HEIGHT,
                    boxSizing: "border-box",
                  }}
                />
                <MailProButton
                  variant="outlined"
                  label="Sort by PROPERTY TYPE"
                  onClick={handleSortByPropertyType}
                  disabled={isSavingOrder || !properties?.length}
                  customStyle={{
                    fontSize: "11px",
                    padding: "5px 12px",
                    textTransform: "none",
                    fontWeight: "600",
                    minWidth: "auto",
                    borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
                    minHeight: DIALOG_BUTTON_MIN_HEIGHT,
                    height: DIALOG_BUTTON_MIN_HEIGHT,
                    boxSizing: "border-box",
                  }}
                />
              </div>
              {onOpenHowToReorder && (
                <div className={classes.reorderSortToolbarRight}>
                  <button
                    type="button"
                    className={classes.howToReorderLink}
                    onClick={onOpenHowToReorder}
                  >
                    How to Reorder Properties
                  </button>
                </div>
              )}
            </div>
            <MiniTableProCore
              {...rest}
              miniTableProperties={properties}
              printVersion={false}
              hideTitleAndSubtitle={true}
              reorderable={true}
              onReorder={onReorder}
              onDragStart={onDragStart}
              maxRows={maxRows}
              currentArea={currentArea}
              quarterlyAreaTable={quarterlyAreaTable}
              agentData={agentData}
            />
          </div>
        </div>
      </MailProDialogBody>
      <div className={classes.dialogFooter}>
        <div className={classes.dialogFooterActions}>
          <div className={classes.dialogFooterActionsLeft}>
            <MailProButton
              variant="outlined"
              label="Cancel"
              onClick={handleCancel}
              disabled={isSavingOrder}
              customStyle={{
                fontSize: "12px",
                padding: "6px 14px",
                textTransform: "uppercase",
                fontWeight: "bold",
                minWidth: "auto",
                borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
                minHeight: DIALOG_BUTTON_MIN_HEIGHT,
                height: DIALOG_BUTTON_MIN_HEIGHT,
                boxSizing: "border-box",
              }}
            />
          </div>
          <div className={classes.dialogFooterActionsCenter}>
            <MailProButton
              variant="filled"
              label={isSavingOrder ? "Saving..." : "Update"}
              onClick={handleUpdate}
              disabled={isSavingOrder}
              customStyle={{
                fontSize: "12px",
                padding: "6px 14px",
                textTransform: "uppercase",
                fontWeight: "bold",
                minWidth: "auto",
                borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
                minHeight: DIALOG_BUTTON_MIN_HEIGHT,
                height: DIALOG_BUTTON_MIN_HEIGHT,
                boxSizing: "border-box",
              }}
            />
          </div>
          <div className={classes.dialogFooterActionsRight} />
        </div>
      </div>
    </MailProDialog>
  );
}
